import { Add, Search } from '@mui/icons-material';
import { Box, Button, TextField, Typography, InputAdornment } from '@mui/material';
import { useState, useMemo } from 'react';
import DataTable from '../DataTable';
import { globalConfirm } from '../GlobalSnackbar';
import Loader from '../Loader';
import { USE_NEW_SHELL } from 'core/flags';
import DataTablePageV2 from '../AppShell/DataTablePageV2';

export type SortDirection = 'asc' | 'desc' | null;

export type FilterType = 'boolean' | 'select';

export interface ColumnFilter {
  type: FilterType;
  options?: { value: string | boolean | null; label: string }[];
}

export interface DataTableColumn {
  key: string;
  label: string;
  render?: (value: unknown | string[], row: unknown) => React.ReactNode;
  sortable?: boolean;
  sortFn?: (a: unknown, b: unknown) => number;
  filterable?: boolean;
  filter?: ColumnFilter;
}

// Базовый интерфейс для строки данных
export interface BaseDataRow {
  id?: string | number;
  [key: string]: unknown;
}

interface DataTablePageProps<T extends BaseDataRow = BaseDataRow> {
  title: string;
  columns: DataTableColumn[];
  data: T[];
  isLoading: boolean;
  onCreate: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  createButtonText?: string;
  emptyMessage?: string;
}

interface SortState {
  columnKey: string | null;
  direction: SortDirection;
}

const DataTablePageLegacy = <T extends BaseDataRow = BaseDataRow>({
  title,
  columns,
  data,
  isLoading,
  onCreate,
  onEdit,
  onDelete,
  createButtonText = 'Создать',
  emptyMessage = 'Данные не найдены',
}: DataTablePageProps<T>) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ columnKey: null, direction: null });
  const [filters, setFilters] = useState<Record<string, string | boolean | null>>({});

  // Обработчик клика по заголовку колонки для сортировки
  const handleSort = (columnKey: string) => {
    const column = columns.find((col) => col.key === columnKey);
    if (!column || !column.sortable) {
      return;
    }

    setSortState((prev) => {
      const newState =
        prev.columnKey === columnKey
          ? prev.direction === 'asc'
            ? { columnKey, direction: 'desc' as SortDirection }
            : prev.direction === 'desc'
              ? { columnKey: null, direction: null }
              : { columnKey, direction: 'asc' as SortDirection }
          : { columnKey, direction: 'asc' as SortDirection };

      return newState;
    });
  };

  // Обработчик изменения фильтра
  const handleFilterChange = (columnKey: string, value: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === 'all' || value === '') {
        delete newFilters[columnKey];
      } else {
        // Преобразуем строку в boolean для булевых фильтров
        const column = columns.find((col) => col.key === columnKey);
        if (column?.filter?.type === 'boolean') {
          newFilters[columnKey] = value === 'true';
        } else if (column?.filter?.type === 'select') {
          // Для select фильтров находим соответствующее значение из options
          const option = column.filter.options?.find((opt) => String(opt.value) === value);
          if (option) {
            newFilters[columnKey] = option.value;
          }
        } else {
          newFilters[columnKey] = value;
        }
      }
      return newFilters;
    });
  };

  // Фильтруем и сортируем данные
  const filteredAndSortedData = useMemo(() => {
    let result = data;

    // Применяем фильтры по колонкам
    result = result.filter((row) => {
      return Object.entries(filters).every(([columnKey, filterValue]) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column || !column.filterable) return true;

        const rowValue = row[columnKey];

        if (column.filter?.type === 'boolean') {
          return rowValue === filterValue;
        }

        if (column.filter?.type === 'select') {
          return rowValue === filterValue;
        }

        return true;
      });
    });

    // Применяем фильтрацию по поисковому запросу
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.toLowerCase().trim();

      // Функция поиска значения в разных типах данных
      const searchInValue = (value: unknown): boolean => {
        if (value === null || value === undefined) {
          return false;
        }

        // Если значение - строка, проверяем на совпадение
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm);
        }

        // Если значение - число, преобразуем в строку и проверяем
        if (typeof value === 'number' || typeof value === 'boolean') {
          return String(value).toLowerCase().includes(searchTerm);
        }

        // Если значение - массив, проверяем каждый элемент
        if (Array.isArray(value)) {
          return value.some((item) => searchInValue(item));
        }

        // Если значение - объект, рекурсивно проверяем все его свойства
        if (typeof value === 'object') {
          return Object.values(value).some((val) => searchInValue(val));
        }

        return false;
      };

      // Ищем только по видимым колонкам
      result = data.filter((row) => {
        return columns.some((column) => {
          const value = row[column.key];
          return searchInValue(value);
        });
      });
    }

    // Применяем сортировку (всегда, если активна)
    if (sortState.columnKey && sortState.direction) {
      const columnKey = sortState.columnKey;
      const sortColumn = columns.find((col) => col.key === columnKey);
      if (sortColumn && sortColumn.sortable) {
        result = [...result].sort((a, b) => {
          const aValue: unknown = a[columnKey];
          const bValue: unknown = b[columnKey];

          let comparison = 0;
          if (sortColumn.sortFn) {
            comparison = sortColumn.sortFn(aValue, bValue);
          } else {
            // Стандартная сортировка - преобразуем в строки для сравнения
            const aStr = String(aValue ?? '');
            const bStr = String(bValue ?? '');
            if (aStr < bStr) comparison = -1;
            else if (aStr > bStr) comparison = 1;
          }

          return sortState.direction === 'asc' ? comparison : -comparison;
        });
      }
    }

    return result;
  }, [data, searchQuery, columns, sortState, filters]);

  const handleEdit = (item: T) => {
    onEdit?.(item);
  };

  const handleDelete = (item: T) => {
    // Определяем название элемента для сообщения
    const itemName = item.title || item.name || item.email || item.code || 'элемент';

    globalConfirm({
      message: `Вы уверены, что хотите удалить "${itemName}"?`,
      title: 'Подтверждение удаления',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      onConfirm: () => onDelete?.(item),
      onCancel: () => {
        // Отмена удаления - ничего не делаем
      },
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 3,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{ flexShrink: 0 }}
      >
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={onCreate}>
          {createButtonText}
        </Button>
      </Box>

      {data && data.length > 0 && (
        <Box mb={2} sx={{ flexShrink: 0 }}>
          <TextField
            fullWidth
            placeholder="Поиск..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {data && data.length > 0 ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataTable<T>
            columns={columns}
            data={filteredAndSortedData}
            onEdit={handleEdit}
            onDelete={handleDelete}
            sortState={sortState}
            onSort={handleSort}
            filters={filters}
            onFilterChange={handleFilterChange}
            emptyMessage={
              filteredAndSortedData.length === 0
                ? searchQuery.trim() || Object.keys(filters).length > 0
                  ? 'По заданным фильтрам ничего не найдено'
                  : emptyMessage
                : undefined
            }
          />
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          sx={{ flexShrink: 0 }}
        >
          <Typography variant="h6" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const DataTablePage = <T extends BaseDataRow = BaseDataRow>(props: DataTablePageProps<T>) => {
  if (USE_NEW_SHELL) {
    return <DataTablePageV2<T> {...props} />;
  }
  return <DataTablePageLegacy<T> {...props} />;
};

export default DataTablePage;
